import { DEFAULT_LOCALE, LOCALES, type Locale } from "verdi-common/i18n";
import { createDashboardLogger, DashboardModuleNames } from "@/logging";
import { LocalStorageKey } from "@/services/local-storage/local-storage";

const Logger = createDashboardLogger(DashboardModuleNames.i18n);

// Map browser language subtags to supported locale codes (e.g. "en" -> "en-CA").
const LANGUAGE_SUBTAG_MAP = {
    en: "en-CA",
    fr: "fr-CA",
    es: "es-MX",
    pt: "pt-PT",
    ja: "ja-JP",
    pa: "pa-IN",
} satisfies Partial<Record<string, Locale>>;

export function isSupportedLocale(language: string | null | undefined): language is Locale {
    return !!language && (LOCALES as readonly string[]).includes(language);
}

function getLocalStorage(): Storage | undefined {
    // window and window.localStorage are guaranteed to exist in a browser/SPA context.
    // The try/catch here is only for the SecurityError a browser can throw when storage
    // access is blocked (e.g. restrictive cookie settings or a sandboxed iframe).
    try {
        return window.localStorage;
    } catch (err) {
        Logger.error({ error: err }, "window.localStorage unavailable");
        return undefined;
    }
}

function getStoredLocale(): Locale | undefined {
    const storage = getLocalStorage();
    if (!storage) {
        return undefined;
    }

    try {
        const stored = storage.getItem(LocalStorageKey.LANGUAGE_SETTING);
        return isSupportedLocale(stored) ? stored : undefined;
    } catch (err) {
        Logger.error({ error: err }, "localStorage.getItem failed");
        return undefined;
    }
}

export function setStoredLocale(locale: Locale): void {
    const storage = getLocalStorage();
    if (!storage) {
        return;
    }

    try {
        storage.setItem(LocalStorageKey.LANGUAGE_SETTING, locale);
    } catch (err) {
        Logger.error({ error: err }, "localStorage.setItem failed");
    }
}

function getSupportedBrowserLocale(language: string | undefined): Locale | undefined {
    if (isSupportedLocale(language)) {
        return language;
    }

    const languagePart = language?.split("-")[0].toLowerCase();
    if (!languagePart) {
        return undefined;
    }

    const mapped = LANGUAGE_SUBTAG_MAP[languagePart as keyof typeof LANGUAGE_SUBTAG_MAP];
    return isSupportedLocale(mapped) ? mapped : undefined;
}

function getBrowserLocale(): Locale | undefined {
    // navigator and navigator.language are guaranteed to exist in a browser/SPA context.
    return getSupportedBrowserLocale(navigator.language);
}

export function getInitialLocale(): Locale {
    return getStoredLocale() ?? getBrowserLocale() ?? DEFAULT_LOCALE;
}
